import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"
import isString from "lodash-es/isString"
import map from "lodash-es/map"

import { getCurrentAccessToken, getCurrentRefreshToken } from "$/utils/tokens"

import { enrollmentAdapter } from "@/3514/adapters/enrollment.adapter"
import type {
    IEnrollmentDto,
    IEnrollmentModel,
    ISharedUserDto,
    ISharedUserModel,
    IUserAssistantDto,
    IUserAssistantModel,
    IUserCoachDto,
    IUserCoachModel,
    IUserDto,
    IUserManagerDto,
    IUserManagerModel,
    IUserModel,
    IUserParticipantDto,
    IUserParticipantModel
} from "@/3514/types"
import { EUserRole } from "@/3514/types"
import { type IJwtTokenPairModel } from "@/models/jwt"

import { getProductTypeInternalNameEnumFromType } from "./product-type.adapter"

function baseAdapter(partialUser: ISharedUserDto): ISharedUserModel {
    return {
        id: partialUser.id,
        email: partialUser.email,
        firstName: partialUser.first_name,
        lastName: partialUser.last_name,
        photo: isString(partialUser.photo) ? partialUser.photo : null,
        jwtPair: isEmpty(partialUser.jwt_pair)
            ? { access: String(""), refresh: String("") }
            : (partialUser.jwt_pair as IJwtTokenPairModel),
        languageCode: partialUser.language_code,
        userId: partialUser.user_id
    }
}

function managerAdapter(managerDto: IUserManagerDto): IUserManagerModel {
    return { ...baseAdapter(managerDto), role: EUserRole.Manager }
}

function coachAdapter(coachDto: IUserCoachDto): IUserCoachModel {
    return {
        ...baseAdapter(coachDto),
        role: EUserRole.Coach,
        isAccountsRelinkingRequired: coachDto.accounts_relinking_required,
        isCoachingPlanAvailable: coachDto.available_coaching_plan,
        isCalendarSetupConfirmed: coachDto.calendar_setup_confirmed,
        areSubUsersPresent: coachDto.has_sub_users
    }
}

function assistantAdapter(assistantDto: IUserAssistantDto): IUserAssistantModel {
    return {
        ...baseAdapter(assistantDto),
        role: EUserRole.Assistant
    }
}

function participantAdapter(participantDto: IUserParticipantDto): IUserParticipantModel {
    return {
        ...baseAdapter(participantDto),
        role: EUserRole.Participant,
        isCoachingPlanAvailable: participantDto.available_coaching_plan,
        browserSessionTtl: participantDto.browser_session_ttl,
        isCoachMatchingFinished: participantDto.coach_matching_finished,
        cohortName: participantDto.cohort,
        cohortLanguages: participantDto.cohort_available_languages,
        doesCohortExist: participantDto.cohort_exists,
        currentModule: isNull(participantDto.current_module) ? null : enrollmentAdapter(participantDto.current_module),
        isEligibleSelectModule: participantDto.eligible_select_module,
        enrollments: isEmpty(participantDto.enrolled_modules)
            ? []
            : map<IEnrollmentDto, IEnrollmentModel>(participantDto.enrolled_modules, enrollmentAdapter),
        areGuestSessionsEnabled: participantDto.guest_sessions_enabled,
        isDiagnosticPresent: participantDto.has_diagnostic,
        isReflectionPresent: participantDto.has_reflection,
        areNotCompletedSurveysPresent: participantDto.has_not_completed_surveys,
        areSurveysPresent: participantDto.has_surveys,
        isSubUser: participantDto.is_sub_user,
        journeyLength: participantDto.journey_length,
        areModulesAvailable: participantDto.module_availability,
        modulesShortList: participantDto.modules,
        isOffboardingPassed: participantDto.offboarding_passed,
        isOnboardingFinished: participantDto.onboarding_finished,
        organizationName: participantDto.organization,
        organizationSlug: participantDto.organization_slug,
        isPracticeLabAvailable: participantDto.practice_lab,
        productType: getProductTypeInternalNameEnumFromType(participantDto.product_type),
        isUnexpectedAuth: participantDto.unexpected_oauth
    }
}

function participantByIdQueryAdapter(dto: IUserParticipantDto): IUserParticipantModel {
    if (isEmpty(dto)) return null

    return participantAdapter(dto)
}

function userQueryAdapter(dto: IUserDto): IUserModel {
    if (isEmpty(dto)) return null

    switch (dto.role) {
        case "participant":
            return participantAdapter(dto)
        case "coach":
            return coachAdapter(dto)
        case "program_manager":
            return managerAdapter(dto)
        case "executive_assistant":
            return assistantAdapter(dto)
        default:
            return null
    }
}

function jwtAdapter(user: IUserModel): IUserModel {
    return isNull(user)
        ? null
        : {
              ...user,
              jwtPair: {
                  access: isEmpty(user.jwtPair.access) ? getCurrentAccessToken() : String(""),
                  refresh: isEmpty(user.jwtPair.refresh) ? getCurrentRefreshToken() : String("")
              }
          }
}

function carryingUserAdapter(userDto: IUserDto): IUserModel {
    return jwtAdapter(userQueryAdapter(userDto))
}

export { carryingUserAdapter as userQueryAdapter, participantByIdQueryAdapter }
